import * as THREE from 'three/webgpu';
import { getMutationFloorY, MUTATION_FLOOR_TOLERANCE } from '@chaos/sim';
import { GRADIENT_3 } from '../ToonMaterials.js';
import { createLavaShaderMaterial } from '../SurfaceShaders.js';

/** Review images must show artwork, never silently certify a loading fallback. */
export async function waitForReviewArtwork(arena) {
  await arena.ready;
  if (arena.assetState !== 'ready' && arena.assetState !== 'none') {
    throw new Error('Arena artwork unavailable; refusing a collision-fallback review capture');
  }
}

/** Fresh resources per rebuild; ArenaRenderer.clear owns their disposal. */
export function buildPitVisual(root, map, asset) {
  const { width, height } = map.visual ?? map.previewScale;
  for (const batch of asset.batches) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(batch.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(batch.normals, 3));
    let material;
    if (batch.lava) {
      const uvs = [];
      for (let i = 0; i < batch.positions.length; i += 3) uvs.push(batch.positions[i] / 3, batch.positions[i + 2] / 3);
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      material = createLavaShaderMaterial();
      // These are recessed pools, not the game's open lava ocean. Waves would
      // rise above the copper lip and disagree with the hazard collision top.
      material.positionNode = null;
    } else {
      const color = new THREE.Color().fromArray(batch.color);
      material = batch.emission
        ? new THREE.MeshBasicNodeMaterial({ color: color.clone().multiplyScalar(Math.min(batch.emission, 1.4)) })
        : new THREE.MeshToonNodeMaterial({ color, gradientMap: GRADIENT_3, emissive: color, emissiveIntensity: 0.08 });
    }
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = `Blender Pit / ${batch.name}`;
    mesh.userData.partId = batch.partId;
    mesh.scale.set(batch.fixedScale ? 1 : width, batch.fixedScale ? 1 : height, batch.fixedScale ? 1 : width);
    mesh.castShadow = !batch.emission && !batch.lava;
    mesh.receiveShadow = !batch.emission && !batch.lava;
    mesh.userData.ownsMaterial = true;
    root.add(mesh);
  }
}

/** Two draw calls for all mutation surfaces, even with thousands of AABBs. */
export function buildMutationGeometry(map) {
  const ground = [], raised = [];
  const floorY = getMutationFloorY(map) ?? -Infinity;
  for (const p of map.platforms) {
    if (!p.solid || !['normal', 'ice'].includes(p.surface.kind)) continue;
    const top = p.y + p.hy;
    if (map.floor.kind !== 'none' && top <= floorY + MUTATION_FLOOR_TOLERANCE) continue;
    const data = top <= floorY + MUTATION_FLOOR_TOLERANCE ? ground : raised;
    const x0 = p.x - p.hx, x1 = p.x + p.hx, z0 = p.z - p.hz, z1 = p.z + p.hz, y = top + .015;
    data.push(x0,y,z0, x0,y,z1, x1,y,z0, x1,y,z0, x0,y,z1, x1,y,z1);
  }
  const geometry = positions => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const uv = [];
    for (let i = 0; i < positions.length; i += 3) uv.push(positions[i], positions[i + 2]);
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.computeVertexNormals();
    return g;
  };
  return { ground: geometry(ground), raised: geometry(raised) };
}

export function mutationVisualState(world) {
  let floor = null, lava = false;
  for (const e of world?.chaos?.applied ?? []) {
    if (world.tick < e.tick || world.tick >= e.untilTick) continue;
    if (['ice_floor', 'conveyor_floor'].includes(e.mutation.kind)) floor = e.mutation;
    if (e.mutation.kind === 'lava_floor') lava = true;
  }
  const raised = floor ? (floor.kind === 'ice_floor' ? 'ice' : 'conveyor') : null;
  return { ground: lava ? 'lava' : raised, raised, floor };
}

/** Visible collision-matched geometry while art is loading or unavailable. */
export function buildPitFallback(root, map) {
  for (const hazard of [false, true]) {
    const platforms = map.platforms.filter(p => p.solid && (p.surface.kind === 'hazard') === hazard);
    const mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicNodeMaterial({ color: hazard ? 0xff4200 : 0x9d713b }), platforms.length);
    const matrix = new THREE.Matrix4();
    platforms.forEach((p, i) => { matrix.makeScale(p.hx * 2, p.hy * 2, p.hz * 2); matrix.setPosition(p.x, p.y, p.z); mesh.setMatrixAt(i, matrix); });
    mesh.userData.ownsMaterial = true;
    mesh.name = 'Pit collision fallback';
    root.add(mesh);
  }
}
